import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Star, Minus, Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const { add } = useCart();
    const { user } = useAuth();

    async function reload() {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        const rel = await api.get("/products", { params: { category: data.category, limit: 8 } });
        setRelated(rel.data.filter((p) => p.id !== data.id).slice(0, 4));
    }

    useEffect(() => {
        reload();
        // eslint-disable-next-line
    }, [id]);

    if (!product) {
        return (
            <>
                <Navbar variant="light" />
                <div className="pt-32 container-edge text-wine-dark/60 label-overline">A carregar…</div>
            </>
        );
    }

    async function submitReview(e) {
        e.preventDefault();
        if (!user) return toast.error("Inicie sessão para deixar uma avaliação.");
        try {
            await api.post("/reviews", { product_id: product.id, rating, comment });
            setComment("");
            toast.success("Avaliação publicada");
            reload();
        } catch (e) {
            toast.error("Erro ao publicar avaliação");
        }
    }

    return (
        <>
            <Navbar variant="light" />
            <div className="pt-28 bg-beige-natural">
                <div className="container-edge py-6 text-xs text-wine/60 label-overline flex items-center gap-2">
                    <Link to="/marketplace" className="hover:text-wine">Marketplace</Link>
                    <ChevronRight className="h-3 w-3" />
                    <span>{product.category}</span>
                </div>
                <div className="container-edge grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 py-8">
                    <div className="space-y-4">
                        <div className="aspect-[4/5] overflow-hidden bg-beige-warm">
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="space-y-6">
                        <span className="label-overline text-wine">{product.region}</span>
                        <h1 className="font-heading text-4xl md:text-6xl text-wine-dark leading-[1.05]">
                            {product.name}
                        </h1>
                        {product.producer && (
                            <Link
                                to={`/produtores/${product.producer.id}`}
                                className="inline-block label-overline text-gold-soft hover:text-wine transition-colors"
                                data-testid="product-producer-link"
                            >
                                Por {product.producer.producer_name} →
                            </Link>
                        )}
                        <div className="flex items-center gap-2">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <Star
                                    key={n}
                                    className={`h-4 w-4 ${n <= Math.round(product.avg_rating) ? "fill-gold-soft text-gold-soft" : "text-wine-dark/30"}`}
                                />
                            ))}
                            <span className="text-xs text-wine-dark/60 ml-2">
                                {product.avg_rating || "Sem avaliações"}
                                {product.reviews?.length > 0 && ` · ${product.reviews.length} avaliações`}
                            </span>
                        </div>
                        <p className="text-wine-dark/75 leading-relaxed">{product.description}</p>
                        <div className="hairline" />
                        <div className="flex items-end justify-between">
                            <span className="font-heading text-5xl text-wine-dark">€{product.price.toFixed(2)}</span>
                            <span className="text-xs text-wine-dark/60 label-overline">
                                {product.stock > 0 ? `${product.stock} em stock` : "Esgotado"}
                            </span>
                        </div>

                        <div className="flex items-center gap-4 pt-4">
                            <div className="flex items-center border border-wine-dark/30">
                                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="qty-minus">
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span className="px-5 text-sm" data-testid="qty-value">{qty}</span>
                                <button onClick={() => setQty(qty + 1)} className="p-3" data-testid="qty-plus">
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                            <button
                                onClick={() => {
                                    add(product, qty);
                                    toast.success(`${qty}× ${product.name} adicionado`);
                                }}
                                disabled={product.stock <= 0}
                                className="btn-wine flex-1"
                                data-testid="add-to-cart-detail"
                            >
                                Adicionar ao carrinho
                            </button>
                        </div>

                        {product.limited_edition && (
                            <div className="bg-wine-dark text-gold-soft px-6 py-4 rounded-md label-overline">
                                Edição limitada · Stock reduzido
                            </div>
                        )}
                    </div>
                </div>

                {/* Reviews */}
                <div className="container-edge py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <h2 className="font-heading text-3xl text-wine-dark mb-8">Avaliações</h2>
                        {product.reviews?.length === 0 && (
                            <p className="text-wine-dark/60">Seja o primeiro a avaliar este produto.</p>
                        )}
                        <div className="space-y-6">
                            {product.reviews?.map((r) => (
                                <div key={r.id} className="border-b border-wine-dark/10 pb-6">
                                    <div className="flex items-center gap-3">
                                        <span className="font-semibold text-wine-dark">{r.user_name}</span>
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star key={n} className={`h-3 w-3 ${n <= r.rating ? "fill-gold-soft text-gold-soft" : "text-wine-dark/30"}`} />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="mt-2 text-sm text-wine-dark/75">{r.comment}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <h3 className="label-overline text-wine">Deixar avaliação</h3>
                        <form onSubmit={submitReview} className="mt-6 space-y-4">
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setRating(n)}
                                        data-testid={`review-star-${n}`}
                                    >
                                        <Star className={`h-6 w-6 ${n <= rating ? "fill-gold-soft text-gold-soft" : "text-wine-dark/30"}`} />
                                    </button>
                                ))}
                            </div>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Partilhe a sua experiência…"
                                rows={4}
                                required
                                className="w-full bg-transparent border border-wine-dark/20 px-4 py-3 text-sm focus:border-wine"
                                data-testid="review-comment"
                            />
                            <button type="submit" className="btn-wine w-full" data-testid="review-submit">
                                Publicar
                            </button>
                        </form>
                    </div>
                </div>

                {/* Related */}
                {related.length > 0 && (
                    <div className="container-edge py-16">
                        <h2 className="font-heading text-3xl text-wine-dark mb-10">Também poderá gostar</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {related.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}
